<template>
  <div class="price-analytics relative inline-block">
    <!-- Trigger icon -->
    <button
      @click="toggleTooltip"
      @mouseenter="isHovering = true"
      @mouseleave="isHovering = false"
      class="price-analytics-trigger ml-2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none"
      type="button"
    >
      <vue-feather type="info" size="16" :class="getIconColor()" />
    </button>

    <teleport to="body">
      <div v-if="isOpen" class="price-analytics-modal-shell" role="dialog" aria-modal="true">
        <div class="price-analytics-modal-backdrop" @click="closeTooltip"></div>
        <div
          class="price-analytics-panel price-analytics-modal-panel w-80 max-w-80 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
          @click.stop
        >
      <!-- Loading state -->
      <div
        v-if="isLoadingAnalytics"
        class="price-analytics-loading flex items-center justify-center py-4"
      >
        <div
          class="inline-flex h-6 w-6 animate-spin rounded-full border-2 border-blue-500 border-r-transparent"
        ></div>
        <span class="ml-2 text-sm text-gray-600 dark:text-gray-400">
          Loading analytics...
        </span>
      </div>

      <!-- No data message when no remaining inventory -->
      <div
        v-else-if="
          currentAnalytics && currentAnalytics.remaining_inventory === 0
        "
        class="price-empty-state text-center py-6"
      >
        <vue-feather
          type="package"
          size="32"
          class="text-gray-400 mx-auto mb-3"
        />
        <p class="text-sm font-medium text-gray-900 dark:text-white mb-2">
          No Stock Available
        </p>
        <p class="text-xs text-gray-500 dark:text-gray-400">
          This product has no remaining inventory. Please restock to see price
          analytics.
        </p>
        <button
          @click="closeTooltip"
          class="price-analytics-close-btn mt-4 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Close
        </button>
      </div>

      <!-- Content -->
      <div v-else-if="currentAnalytics">
        <!-- Header -->
        <div class="price-analytics-header flex items-center justify-between mb-3">
          <h4 class="text-sm font-semibold text-gray-900 dark:text-white">
            <vue-feather type="trending-up" size="14" class="inline mr-1" />
            Price Analytics
          </h4>
          <button
            @click="closeTooltip"
            class="price-analytics-x text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <vue-feather type="x" size="16" />
          </button>
        </div>

        <!-- Analytics data -->
        <div class="price-analytics-body space-y-3">
          <div class="price-product-card">
            <span>Selected product</span>
            <strong>{{ productName || `Product #${productId}` }}</strong>
          </div>

          <!-- Current Price -->
          <div class="price-current-metric pb-3 border-b border-gray-200 dark:border-gray-700">
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-500 dark:text-gray-400">
                Current Price
              </span>
              <span class="text-sm font-semibold text-gray-900 dark:text-white">
                {{ formatPrice(currentPrice) }}
              </span>
            </div>
          </div>

          <!-- Purchase Price Range -->
          <div class="price-metric-grid space-y-2">
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-500 dark:text-gray-400">
                Remaining Stock
              </span>
              <span
                class="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {{ formatQuantity(currentAnalytics.remaining_inventory) }}
              </span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-500 dark:text-gray-400">
                Min Purchase Price
              </span>
              <span
                class="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {{ formatPrice(currentAnalytics.min_purchase_unit_price) }}
              </span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-500 dark:text-gray-400">
                Max Purchase Price
              </span>
              <span
                class="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {{ formatPrice(currentAnalytics.max_purchase_unit_price) }}
              </span>
            </div>
          </div>

          <!-- Warning if price is below min -->
          <div
            v-if="isPriceBelowMin"
            class="price-alert-card price-alert-card--red p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md overflow-hidden"
          >
            <div class="flex items-start">
              <vue-feather
                type="alert-triangle"
                size="16"
                class="text-red-600 dark:text-red-400 mt-0.5 mr-2 flex-shrink-0"
              />
              <div class="flex-1 min-w-0 overflow-hidden">
                <p
                  class="text-xs font-medium text-red-800 dark:text-red-300 break-words leading-tight"
                  style="
                    word-break: break-word;
                    overflow-wrap: anywhere;
                    hyphens: auto;
                    max-width: 100%;
                  "
                >
                  Price Below Cost!
                </p>
                <p
                  class="text-xs text-red-700 dark:text-red-400 mt-1 break-words leading-tight"
                  style="
                    word-break: break-word;
                    overflow-wrap: anywhere;
                    hyphens: auto;
                    max-width: 100%;
                  "
                >
                  Your current price ({{ formatPrice(currentPrice) }}) is below
                  the minimum purchase cost. You're selling at a loss.
                </p>
                <p
                  class="text-xs text-red-600 dark:text-red-500 mt-1 break-words"
                  style="word-break: break-word; overflow-wrap: anywhere"
                >
                  Suggested: {{ formatPrice(suggestedPrice) }}
                </p>
              </div>
            </div>
          </div>

          <!-- Warning if price is below max (potential profit loss) -->
          <div
            v-else-if="isPriceBelowMax"
            class="price-alert-card price-alert-card--yellow p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md overflow-hidden"
          >
            <div class="flex items-start">
              <vue-feather
                type="alert-circle"
                size="16"
                class="text-yellow-600 dark:text-yellow-400 mt-0.5 mr-2 flex-shrink-0"
              />
              <div class="flex-1 min-w-0 overflow-hidden">
                <p
                  class="text-xs font-medium text-yellow-800 dark:text-yellow-300 break-words"
                  style="word-break: break-word; overflow-wrap: anywhere"
                >
                  Low Margin Alert
                </p>
                <p
                  class="text-xs text-yellow-700 dark:text-yellow-400 mt-1 break-words"
                  style="word-break: break-word; overflow-wrap: anywhere"
                >
                  Your price is below the maximum purchase cost. Consider
                  increasing for better margins.
                </p>
                <p
                  class="text-xs text-yellow-600 dark:text-yellow-500 mt-1 break-words"
                  style="word-break: break-word; overflow-wrap: anywhere"
                >
                  Suggested: {{ formatPrice(suggestedPrice) }}
                </p>
              </div>
            </div>
          </div>

          <!-- Close to max (alerting) -->
          <div
            v-else-if="isCloseToMax"
            class="price-alert-card price-alert-card--orange p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md overflow-hidden"
          >
            <div class="flex items-start">
              <vue-feather
                type="alert-triangle"
                size="16"
                class="text-orange-600 dark:text-orange-400 mt-0.5 mr-2 flex-shrink-0"
              />
              <div class="flex-1 min-w-0 overflow-hidden">
                <p
                  class="text-xs font-medium text-orange-800 dark:text-orange-300 break-words"
                  style="word-break: break-word; overflow-wrap: anywhere"
                >
                  Margin Warning
                </p>
                <p
                  class="text-xs text-orange-700 dark:text-orange-400 mt-1 break-words"
                  style="word-break: break-word; overflow-wrap: anywhere"
                >
                  Your price is only slightly above the maximum purchase cost
                  (less than 10% margin). Consider increasing.
                </p>
                <p
                  class="text-xs text-orange-600 dark:text-orange-500 mt-1 break-words"
                  style="word-break: break-word; overflow-wrap: anywhere"
                >
                  Suggested: {{ formatPrice(suggestedPrice) }}
                </p>
              </div>
            </div>
          </div>
<!-- Good pricing -->
<div
  v-else
  class="price-alert-card price-alert-card--green p-[10px_4px] bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md"
>
  <div class="flex items-start space-x-2">
    <vue-feather
      type="check-circle"
      size="16"
      class="text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0"
    />
    <div class="flex-1 min-w-0">
      <p
        class="text-xs font-medium text-green-800 dark:text-green-300 leading-tight break-words"
      >
        Good Pricing
      </p>
      <p
        class="text-xs text-green-700 dark:text-green-400 mt-1 leading-tight break-words"
      >
        Your price provides a healthy profit margin.
      </p>
    </div>
  </div>
</div>


          <!-- Update Price Section -->
          <div class="price-update-card pt-3 border-t border-gray-200 dark:border-gray-700">
            <div class="price-update-row flex items-center gap-2">
              <input
                v-model.number="newPrice"
                type="number"
                step="0.01"
                min="0"
                placeholder="New price"
                class="price-update-input flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                @click="updatePrice"
                :disabled="updating || !isValidNewPrice"
                class="price-update-button px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span
                  v-if="updating"
                  class="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent"
                ></span>
                <span v-else>Update</span>
              </button>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Enter a new price to update instantly
            </p>
          </div>
        </div>
      </div>

      <!-- Error state -->
      <div
        v-else-if="error"
        class="p-4 text-center text-sm text-gray-700 dark:text-gray-300"
      >
        <vue-feather
          type="info"
          size="28"
          class="text-gray-400 mx-auto mb-3"
        />
        <p class="font-medium text-gray-900 dark:text-white mb-1">
          {{ error }}
        </p>
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-4">
          You can still update the current sale price for this product.
        </p>
        <div class="price-update-row flex items-center gap-2">
          <input
            v-model.number="newPrice"
            type="number"
            step="0.01"
            min="0"
            placeholder="New price"
            class="price-update-input flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            @click="updatePrice"
            :disabled="updating || !isValidNewPrice"
            class="price-update-button px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span
              v-if="updating"
              class="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent"
            ></span>
            <span v-else>Update</span>
          </button>
        </div>
      </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import { ref, computed } from "vue";
import http from "@/helper/http";
import { toast } from "vue3-toastify";

export default {
  name: "PriceAnalyticsTooltip",
  props: {
    productId: {
      type: Number,
      required: true,
    },
    productName: {
      type: String,
      default: "",
    },
    storeId: {
      type: Number,
      required: true,
    },
    currentPrice: {
      type: Number,
      required: true,
    },
    analytics: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["price-updated", "toggle", "fetch-analytics"],
  setup(props, { emit }) {
    const isHovering = ref(false);
    const updating = ref(false);
    const error = ref("");
    const newPrice = ref(null);
    const localLoading = ref(false);
    const localAnalytics = ref(null);

    // Use local analytics if available, otherwise use prop analytics
    const currentAnalytics = computed(
      () => localAnalytics.value || props.analytics
    );

    const isLoadingAnalytics = computed(
      () => localLoading.value || props.loading
    );

    const isPriceBelowMin = computed(() => {
      if (!currentAnalytics.value) return false;
      return (
        props.currentPrice < currentAnalytics.value.min_purchase_unit_price
      );
    });

    const isPriceBelowMax = computed(() => {
      if (!currentAnalytics.value || isPriceBelowMin.value) return false;
      return (
        props.currentPrice < currentAnalytics.value.max_purchase_unit_price
      );
    });

    const isCloseToMax = computed(() => {
      if (
        !currentAnalytics.value ||
        isPriceBelowMin.value ||
        isPriceBelowMax.value
      ) {
        return false;
      }
      const margin =
        (props.currentPrice - currentAnalytics.value.max_purchase_unit_price) /
        currentAnalytics.value.max_purchase_unit_price;
      return margin < 0.1; // Within 10% of max cost
    });

    const suggestedPrice = computed(() => {
      if (!currentAnalytics.value) return 0;
      // Suggest 30% markup on max purchase price
      const markup = 1.3;
      return currentAnalytics.value.max_purchase_unit_price * markup;
    });

    const isValidNewPrice = computed(() => {
      return (
        newPrice.value !== null &&
        newPrice.value !== undefined &&
        newPrice.value !== "" &&
        Number(newPrice.value) >= 0
      );
    });

    const getIconColor = () => {
      if (!currentAnalytics.value) return "text-gray-400";
      if (isPriceBelowMin.value) return "text-red-500";
      if (isPriceBelowMax.value) return "text-yellow-500";
      if (isCloseToMax.value) return "text-orange-500";
      return "text-green-500";
    };

    const formatPrice = (price) => {
      if (!price) return "RWF 0";
      return `RWF ${parseFloat(price).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}`;
    };

    const formatQuantity = (qty) => {
      if (!qty) return "0";
      return parseFloat(qty).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    };

    const fetchIndividualAnalytics = async () => {
      localLoading.value = true;
      error.value = "";

      try {
        const response = await http.get(
          `/v2/store/${props.storeId}/products/purchase-price-ranges?product_ids=${props.productId}`
        );

        if (response.data && response.data[props.productId]) {
          localAnalytics.value = response.data[props.productId];
        } else {
          error.value = "No analytics data available";
        }
      } catch (err) {
        console.error("Failed to fetch individual price analytics", err);
        error.value = "Failed to load analytics";
      } finally {
        localLoading.value = false;
      }
    };

    const toggleTooltip = () => {
      emit("toggle");
      // If opening and no analytics data, fetch it
      if (!props.isOpen && !props.analytics && !localAnalytics.value) {
        fetchIndividualAnalytics();
      }
    };

    const closeTooltip = () => {
      emit("toggle");
    };

    const updatePrice = async () => {
      if (!isValidNewPrice.value) return;

      updating.value = true;
      try {
        await http.post("/v1/store/product/sale/price", {
          product_id: props.productId,
          unit_price: Number(newPrice.value),
        });

        toast.success("Price updated successfully!");
        emit("price-updated", {
          productId: props.productId,
          newPrice: Number(newPrice.value),
        });

        // Close tooltip after successful update
        newPrice.value = null;
        closeTooltip();
      } catch (err) {
        console.error("Failed to update price", err);
        toast.error(
          err?.response?.data?.error?.message ||
            "Failed to update price. Please try again."
        );
      } finally {
        updating.value = false;
      }
    };

    return {
      isHovering,
      updating,
      error,
      newPrice,
      localLoading,
      localAnalytics,
      currentAnalytics,
      isLoadingAnalytics,
      isPriceBelowMin,
      isPriceBelowMax,
      isCloseToMax,
      suggestedPrice,
      isValidNewPrice,
      getIconColor,
      formatPrice,
      formatQuantity,
      fetchIndividualAnalytics,
      toggleTooltip,
      closeTooltip,
      updatePrice,
    };
  },
};
</script>

<style scoped>
.price-analytics-trigger {
  align-items: center;
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--hs-primary, #3293ec) 18%, transparent);
  display: inline-flex;
  height: 28px;
  justify-content: center;
  transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  width: 28px;
}

.price-analytics-trigger:hover {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 16%, transparent);
  box-shadow: 0 10px 22px color-mix(in srgb, var(--hs-primary, #3293ec) 18%, transparent);
  transform: translateY(-1px);
}

.price-analytics-panel {
  background:
    radial-gradient(circle at 92% 0%, color-mix(in srgb, var(--hs-primary, #3293ec) 14%, transparent), transparent 34%),
    var(--hs-surface, #ffffff);
  border-color: var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 22px;
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.28);
  backdrop-filter: blur(14px);
}

.price-analytics-modal-shell {
  align-items: center;
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 18px;
  position: fixed;
  z-index: 5000;
}

.price-analytics-modal-backdrop {
  background: rgba(15, 23, 42, 0.54);
  backdrop-filter: blur(8px);
  inset: 0;
  position: fixed;
}

.price-analytics-modal-panel {
  max-height: min(86vh, 680px);
  max-width: min(440px, 100%);
  overflow-y: auto;
  position: relative;
  width: min(440px, 100%);
  z-index: 1;
}

.price-analytics-header {
  border-bottom: 1px solid var(--hs-border, rgba(148, 163, 184, 0.18));
  margin: -4px -4px 12px;
  padding: 2px 2px 12px;
}

.price-analytics-header h4 {
  align-items: center;
  color: var(--hs-text, #0f172a);
  display: inline-flex;
  font-weight: 950;
  gap: 6px;
}

.price-analytics-x,
.price-analytics-close-btn {
  border-radius: 999px;
  transition: background 0.2s ease, transform 0.2s ease;
}

.price-analytics-x {
  align-items: center;
  background: color-mix(in srgb, var(--hs-muted, #64748b) 10%, transparent);
  display: inline-flex;
  height: 30px;
  justify-content: center;
  width: 30px;
}

.price-analytics-x:hover,
.price-analytics-close-btn:hover {
  transform: translateY(-1px);
}

.price-current-metric {
  background: color-mix(in srgb, var(--hs-bg-soft, #f1f5f9) 68%, var(--hs-surface, #ffffff));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.2));
  border-radius: 16px;
  padding: 12px;
}

.price-product-card {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 9%, var(--hs-surface, #ffffff));
  border: 1px solid color-mix(in srgb, var(--hs-primary, #3293ec) 16%, var(--hs-border, rgba(148, 163, 184, 0.22)));
  border-radius: 16px;
  display: grid;
  gap: 5px;
  padding: 12px;
}

.price-product-card span {
  color: var(--hs-muted, #64748b);
  font-size: 0.72rem;
  font-weight: 950;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.price-product-card strong {
  color: var(--hs-text, #0f172a);
  font-size: 1rem;
  font-weight: 950;
  line-height: 1.25;
}

.price-metric-grid {
  background: color-mix(in srgb, var(--hs-surface, #ffffff) 86%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.18));
  border-radius: 16px;
  padding: 12px;
}

.price-alert-card {
  border-radius: 16px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
}

.price-update-card {
  background: color-mix(in srgb, var(--hs-bg-soft, #f1f5f9) 58%, var(--hs-surface, #ffffff));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.18));
  border-radius: 18px;
  margin-top: 4px;
  padding: 12px;
}

.price-update-input {
  border-radius: 14px;
  font-weight: 850;
  min-height: 40px;
}

.price-update-button {
  background: linear-gradient(135deg, var(--hs-primary, #3293ec), #2563eb);
  border-radius: 14px;
  box-shadow: 0 12px 26px color-mix(in srgb, var(--hs-primary, #3293ec) 24%, transparent);
  font-weight: 950;
  min-height: 40px;
  transition: box-shadow 0.2s ease, transform 0.2s ease, opacity 0.2s ease;
}

.price-update-button:not(:disabled):hover {
  box-shadow: 0 16px 34px color-mix(in srgb, var(--hs-primary, #3293ec) 32%, transparent);
  transform: translateY(-1px);
}

.price-empty-state {
  background: color-mix(in srgb, var(--hs-bg-soft, #f1f5f9) 62%, transparent);
  border-radius: 18px;
}

/* Tooltip arrow */
.absolute::after {
  content: "";
  position: absolute;
  top: 100%;
  right: 20px;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: transparent;
}

/* Ensure all text wraps properly */
p {
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
  max-width: 100%;
}
</style>
